import React from 'react';
import { RefreshCw, X, Sparkles, Bell, MessageCircle } from 'lucide-react';
import { useRefresh } from '../context/RefreshContext';
import { Button } from './ui/button';

const UpdateBanner = () => {
  const { 
    showUpdateBanner, 
    setShowUpdateBanner, 
    hasNewContent, 
    contentUpdates, 
    refreshPage,
    isRefreshing 
  } = useRefresh();

  // Don't show if nothing to display
  if (!showUpdateBanner && !hasNewContent) return null;

  return (
    <>
      {/* Theme Update Banner */}
      {showUpdateBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg animate-slide-down">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="font-semibold text-sm">Website Design Updated!</p>
                <p className="text-xs text-white/80">The site has a new look. Refresh to see changes.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => refreshPage()}
                disabled={isRefreshing}
                className="bg-white text-purple-600 hover:bg-white/90"
              >
                {isRefreshing ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Refresh Now
              </Button>
              <button 
                onClick={() => setShowUpdateBanner(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Content Banner */}
      {hasNewContent && !showUpdateBanner && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] animate-bounce-in">
          <button
            onClick={() => refreshPage()}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            {isRefreshing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Bell className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {contentUpdates.messages > 0 && (
                <span className="mr-1">
                  {contentUpdates.messages} new message{contentUpdates.messages > 1 ? 's' : ''}
                </span>
              )}
              {contentUpdates.interests > 0 && (
                <span>
                  {contentUpdates.messages > 0 ? '• ' : ''}
                  {contentUpdates.interests} new interest{contentUpdates.interests > 1 ? 's' : ''}
                </span>
              )}
              {contentUpdates.messages === 0 && contentUpdates.interests === 0 && (
                <span>New content available</span>
              )}
            </span>
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        @keyframes bounce-in {
          0% {
            transform: translateX(-50%) scale(0) translateY(20px);
            opacity: 0;
          }
          50% {
            transform: translateX(-50%) scale(1.1) translateY(-5px);
          }
          100% {
            transform: translateX(-50%) scale(1) translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
        
        .animate-bounce-in {
          animation: bounce-in 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default UpdateBanner;
